import { Routes, Route, Navigate } from "react-router-dom"
import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { onAuthStateChanged } from "firebase/auth"
import { auth } from "./firebase"

import { setCredentials } from "./app/authSlice"

// Public
import PublicRoutes from "./routes/PublicRoutes"
import AuthLayout from "./layouts/AuthLayout"
import Login from "./features/auth/Login"
import ForgotPassword from "./features/auth/ForgotPassword"
import CreateAccount from "./features/auth/CreateAccount"

// Private
import PrivateRoutes from "./routes/PrivateRoutes"
import PrivateLayout from "./layouts/PrivateLayout"
import { ModalProvider } from "./context/ModalContext"

// User
import UserLayout from "./layouts/userLayout/UserLayout"
import UserDashboard from "./features/user/pages/UserDashboard"
import UpdateEmailPage from "./features/user/pages/UpdateEmailPage"
import UpdateUserDetailsPage from "./features/user/pages/UpdateUserDetailsPage"
import NotificationsPage from "./features/user/pages/NotificationsPage"

// Admin
import ViewMembersPage from "./features/admin/ViewMembersPage"
import AdminDashboard from "./features/admin/AdminDashboard"
import AdminLayout from "./layouts/AdminLayout"
import ViewClubsList from "./features/admin/ViewClubsList"
import ViewUserPage from "./features/admin/ViewUserPage"
import CreateClubPage from "./features/admin/pages/CreateClubPage"
import CreateUserPage from "./features/admin/pages/CreateUserPage"
import AdminNotifications from "./features/admin/pages/NotificationsPage"

// Club
import ViewClubPage from "./features/club/ViewClubPage"

// Misc
import PaymentsLayout from "./layouts/PaymentsLayout"
import StripeReturnPage from "./features/payments/StripeReturnPage"
import StripePaymentForm from "./features/payments/StripePaymentForm"
import LoggedOutPage from "./components/LoggedOutPage"
import SplashScreen from "./components/SplashScreen"
import UserClubsPage from "./features/user/pages/UserClubsPage"

function App() {
  const dispatch = useDispatch()
  const [authLoading, setAuthLoading] = useState(true)
  const [splashFading, setSplashFading] = useState(false)
  const [showSplash, setShowSplash] = useState(true)
  const splashStart = useRef(Date.now())
  const splashTimer = useRef(null)
  const fadeTimer = useRef(null)
  const MIN_SPLASH_MS = 1500

  useEffect(() => {
    if (!authLoading) {
      const elapsed = Date.now() - splashStart.current
      const remaining = Math.max(0, MIN_SPLASH_MS - elapsed)

      fadeTimer.current = setTimeout(() => {
        setSplashFading(true)
        splashTimer.current = setTimeout(() => setShowSplash(false), 500)
      }, remaining)
    }
    return () => {
      clearTimeout(fadeTimer.current)
      clearTimeout(splashTimer.current)
    }
  }, [authLoading])

  useEffect(() => {
    // Listen for login or logout and change state accordingly
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const { uid, email, emailVerified } = user

          const idTokenResult = await user.getIdTokenResult()
          const { role } = idTokenResult.claims

          dispatch(setCredentials({
            emailVerified,
            uid,
            email,
            role: role || "user",
            isAuthenticated: true
          }))

        } catch (error) {
          console.error("Error getting user data:", error)
        }
      } else {
        dispatch(setCredentials(null))
      }

      setAuthLoading(false)
    })

    return () => unsubscribe()
  }, [dispatch])

  return (
    <>
      {showSplash && <SplashScreen fading={splashFading} />}
      {!authLoading && <Routes>
      {/* Public Paths */}
      <Route element={<PublicRoutes />}>
        <Route path="/login" element={<AuthLayout />}>
          <Route index element={<Login />} />
          <Route path="create-account" element={<CreateAccount />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
        </Route>
      </Route>
      {/* End Public Paths */}

      {/* Private Paths */}
      <Route element={<ModalProvider />}>
        <Route element={<PrivateRoutes />}>
          <Route path="/" element={<PrivateLayout />} >

            {/* User Paths */}
            <Route path="user" element={<UserLayout />}>
              <Route index element={<UserDashboard />} />
              <Route path="update-details" element={<UpdateUserDetailsPage />} />
              <Route path="change-email" element={<UpdateEmailPage />} />
              <Route path="clubs">
                <Route index element={<UserClubsPage />} />
                <Route path="view-club/:id" element={<ViewClubPage />} />
              </Route>
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>

            {/* Admin Paths */}
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />

              <Route path="members">
                <Route index element={<ViewMembersPage />} />
                <Route path="view-user/:id" element={<ViewUserPage />} />
                <Route path="create-account" element={<CreateUserPage />} />
              </Route>

              <Route path="clubs">
                <Route index element={<ViewClubsList />} />
                <Route path="view-club/:id" element={<ViewClubPage />} />
                <Route path="create-club" element={<CreateClubPage />} />
              </Route>

              <Route path="notifications" element={<AdminNotifications />} />
            </Route>

            {/* Payments Paths */}
            <Route path="payments" element={<PaymentsLayout />}>
              <Route index element={<StripePaymentForm />} />
              <Route path="stripe-return" element={<StripeReturnPage />} />
            </Route>
          </Route>
        </Route>
      </Route>
      {/* End Private Paths */}

      <Route path="error" element={<LoggedOutPage />} />
      <Route path="*" element={<Navigate to={"/login"} replace />} />
    </Routes>}
    </>
  )
}

export default App